/** Figma 9227:111011 — C (P_new) conditional-pending coordination UI. */

import type { ChildLinkRequestItem } from "@services/facilityNetworkLink";
import { expiryDaysFromCreatedAt } from "./parentLinkRequestUtils";

export type CoordinationStage = "awaiting_child" | "awaiting_parent";

export type ConditionalPendingRequestData = {
  requestId: number;
  childFacilityName: string;
  currentParentFacilityName: string;
  stage: CoordinationStage;
  createdAt?: string | null;
  expiresInDays: number | null;
  npi?: string | null;
  ccn?: string | null;
};

export type TrackerStepState = "done" | "active" | "pending";

export type TrackerStep = {
  number: 1 | 2 | 3 | 4;
  state: TrackerStepState;
  title: string;
  detail: string;
};

export function firstFacilityWord(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

export function mapOutgoingToConditionalPending(
  row: ChildLinkRequestItem,
): ConditionalPendingRequestData | null {
  if (!row.coordination_mode) return null;
  if (row.status !== "link_pending" && row.status !== "pending") return null;
  const childName = (row.facility_name || "").trim() || `NPI ${row.target_npi}`;
  const parentName = (row.current_parent_facility_name || "").trim() || "their current parent";
  const stage: CoordinationStage =
    row.coordination_stage === "awaiting_parent" || Boolean(row.coordination_unlink_id)
      ? "awaiting_parent"
      : "awaiting_child";
  return {
    requestId: row.id,
    childFacilityName: childName,
    currentParentFacilityName: parentName,
    stage,
    createdAt: row.created_at,
    expiresInDays: expiryDaysFromCreatedAt(row.created_at),
    npi: row.npi_number || row.target_npi,
    ccn: row.ccn_number,
  };
}

export function formatSentDate(iso?: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/** Build 4-step tracker for Figma conditional pending panel. */
export function buildConditionalPendingSteps(
  data: ConditionalPendingRequestData,
): TrackerStep[] {
  const child = data.childFacilityName;
  const parent = data.currentParentFacilityName;
  const sent = formatSentDate(data.createdAt);
  const awaitingParent = data.stage === "awaiting_parent";

  return [
    {
      number: 1,
      state: "done",
      title: `You sent a link request to ${child}`,
      detail: `${sent} · System detected existing parent relationship`,
    },
    {
      number: 2,
      state: awaitingParent ? "done" : "active",
      title: awaitingParent
        ? `${child} accepted your request`
        : `${child} must accept your request`,
      detail: awaitingParent
        ? "Accepted · unlink request sent to current parent"
        : "Awaiting response · All facility admins notified",
    },
    {
      number: 3,
      state: awaitingParent ? "active" : "pending",
      title: `${parent} must approve the unlink`,
      detail: awaitingParent
        ? "Awaiting response · Current parent admins notified"
        : `Will be notified once ${child} accepts your request`,
    },
    {
      number: 4,
      state: "pending",
      title: "Link established",
      detail: `${child} will appear in your Children tab`,
    },
  ];
}
